import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Bed, Bath, Square, MapPin, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import BookingModal from '@/components/BookingModal';
import SaveSearchButton from '@/components/SaveSearchButton';

const MLSPropertySearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;
  
  // Search filters
  const [filters, setFilters] = useState({
    cities: searchParams.get('cities') || '',
    minprice: searchParams.get('minprice') || '',
    maxprice: searchParams.get('maxprice') || '',
    minbeds: searchParams.get('minbeds') || '',
    maxbeds: searchParams.get('maxbeds') || '',
    minbaths: searchParams.get('minbaths') || '',
    type: searchParams.get('type') || '',
    status: searchParams.get('status') || 'Active',
    sort: searchParams.get('sort') || '-listdate'
  });

  // Tennessee cities we serve
  const tennesseeCities = [
    'Memphis', 'Germantown', 'Collierville', 'Bartlett', 'Lakeland',
    'Arlington', 'Cordova', 'Eads', 'Millington', 'Covington'
  ];

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (filters.cities) params.append('cities', filters.cities);
      if (filters.minprice) params.append('minprice', filters.minprice);
      if (filters.maxprice) params.append('maxprice', filters.maxprice);
      if (filters.minbeds) params.append('minbeds', filters.minbeds);
      if (filters.maxbeds) params.append('maxbeds', filters.maxbeds);
      if (filters.minbaths) params.append('minbaths', filters.minbaths);
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.sort) params.append('sort', filters.sort);
      
      params.append('limit', pageSize.toString());
      params.append('offset', (currentPage * pageSize).toString());
      
      const response = await api.get(`/mls/properties?${params.toString()}`);
      
      if (response.data.error) {
        setError(response.data.error);
        setProperties([]);
        setTotalCount(0);
      } else {
        setProperties(response.data.properties || []);
        setTotalCount(response.data.total_count || 0);
      }
    } catch (err) {
      console.error('Error fetching MLS properties:', err);
      setError('Failed to load properties. Please try again.');
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params);
    
    fetchProperties();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      cities: '',
      minprice: '',
      maxprice: '',
      minbeds: '',
      maxbeds: '',
      minbaths: '',
      type: '',
      status: 'Active',
      sort: '-listdate'
    });
    setSearchParams({});
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mls-search-page">
      <SEO 
        title="Search Homes for Sale | Tennessee & Mississippi | Bespoke Realty"
        description="Browse live MLS listings in Memphis, Germantown, Collierville, Southaven, Olive Branch and surrounding areas. Find your dream home with Bespoke Realty."
        keywords="MLS listings Memphis, homes for sale Tennessee Mississippi, Memphis real estate, Germantown homes, Collierville real estate, Southaven homes, Olive Branch MS"
      />
      
      {/* Hero Banner */}
      <section className="relative py-16 bg-gradient-to-r from-[#434345] to-[#5a5a5c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="mls-heading">
              Browse Available Properties
            </h1>
          </div>
        </div>
      </section>

      {/* Search Form - Clean Horizontal Bar (Matching Buy Page Style) */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch}>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {/* City */}
              <Select 
                value={filters.cities} 
                onValueChange={(value) => handleFilterChange('cities', value === 'all' ? '' : value)}
              >
                <SelectTrigger data-testid="city-select" className="w-[160px] h-11 bg-white border-gray-300">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">— Tennessee —</div>
                  {tennesseeCities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">— Mississippi —</div>
                  <SelectItem value="Olive Branch">Olive Branch</SelectItem>
                  <SelectItem value="Southaven">Southaven</SelectItem>
                  <SelectItem value="Nesbit">Nesbit</SelectItem>
                  <SelectItem value="Hernando">Hernando</SelectItem>
                  <SelectItem value="Byhalia">Byhalia</SelectItem>
                </SelectContent>
              </Select>

              {/* Min Price */}
              <Select 
                value={filters.minprice} 
                onValueChange={(value) => handleFilterChange('minprice', value === 'any' ? '' : value)}
              >
                <SelectTrigger className="w-[140px] h-11 bg-white border-gray-300">
                  <SelectValue placeholder="Min Price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Min Price</SelectItem>
                  <SelectItem value="100000">$100,000</SelectItem>
                  <SelectItem value="200000">$200,000</SelectItem>
                  <SelectItem value="300000">$300,000</SelectItem>
                  <SelectItem value="400000">$400,000</SelectItem>
                  <SelectItem value="500000">$500,000</SelectItem>
                  <SelectItem value="750000">$750,000</SelectItem>
                  <SelectItem value="1000000">$1,000,000</SelectItem>
                </SelectContent>
              </Select>

              {/* Max Price */}
              <Select 
                value={filters.maxprice} 
                onValueChange={(value) => handleFilterChange('maxprice', value === 'any' ? '' : value)}
              >
                <SelectTrigger className="w-[140px] h-11 bg-white border-gray-300">
                  <SelectValue placeholder="Max Price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Max Price</SelectItem>
                  <SelectItem value="200000">$200,000</SelectItem>
                  <SelectItem value="300000">$300,000</SelectItem>
                  <SelectItem value="400000">$400,000</SelectItem>
                  <SelectItem value="500000">$500,000</SelectItem>
                  <SelectItem value="750000">$750,000</SelectItem>
                  <SelectItem value="1000000">$1,000,000</SelectItem>
                  <SelectItem value="1500000">$1,500,000</SelectItem>
                  <SelectItem value="2000000">$2,000,000+</SelectItem>
                </SelectContent>
              </Select>

              {/* Min Bedrooms */}
              <Select 
                value={filters.minbeds} 
                onValueChange={(value) => handleFilterChange('minbeds', value === 'any' ? '' : value)}
              >
                <SelectTrigger data-testid="beds-select" className="w-[150px] h-11 bg-white border-gray-300">
                  <SelectValue placeholder="Min Bedrooms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Min Bedrooms</SelectItem>
                  <SelectItem value="1">1+ Bedrooms</SelectItem>
                  <SelectItem value="2">2+ Bedrooms</SelectItem>
                  <SelectItem value="3">3+ Bedrooms</SelectItem>
                  <SelectItem value="4">4+ Bedrooms</SelectItem>
                  <SelectItem value="5">5+ Bedrooms</SelectItem>
                </SelectContent>
              </Select>

              {/* Property Type */}
              <Select 
                value={filters.type} 
                onValueChange={(value) => handleFilterChange('type', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[150px] h-11 bg-white border-gray-300">
                  <SelectValue placeholder="Property Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Property Type</SelectItem>
                  <SelectItem value="RES">Residential</SelectItem>
                  <SelectItem value="CND">Condo</SelectItem>
                  <SelectItem value="LND">Land</SelectItem>
                  <SelectItem value="MUL">Multi-Family</SelectItem>
                </SelectContent>
              </Select>

              {/* Search Button */}
              <Button 
                type="submit" 
                className="bg-[#BCA480] hover:bg-[#a89470] h-11 px-6"
                disabled={loading}
                data-testid="search-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Header */}
          <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
            <p className="text-gray-600" data-testid="results-count">
              {loading ? 'Searching...' : `${totalCount.toLocaleString()} properties found`}
            </p>
            <SaveSearchButton
              filters={{
                cities: filters.cities || null,
                minprice: filters.minprice ? parseInt(filters.minprice) : null,
                maxprice: filters.maxprice ? parseInt(filters.maxprice) : null,
                minbeds: filters.minbeds ? parseInt(filters.minbeds) : null,
                maxbeds: filters.maxbeds ? parseInt(filters.maxbeds) : null,
                minbaths: filters.minbaths ? parseInt(filters.minbaths) : null,
                type: filters.type || null,
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="error-message">
              <p><strong>Error:</strong> {error}</p>
              <p className="text-sm mt-1">Please check your search criteria and try again.</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#BCA480]" />
              <span className="ml-3 text-gray-600">Loading properties...</span>
            </div>
          )}

          {/* Property Grid */}
          {!loading && properties.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="property-grid">
              {properties.map((property) => (
                <div 
                  key={property.mlsId} 
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/mls/${property.mlsId}`)}
                  data-testid={`property-card-${property.mlsId}`}
                >
                  {/* Property Image */}
                  <div className="relative h-48 bg-gray-200 overflow-hidden">
                    {property.primaryPhoto ? (
                      <img 
                        src={property.primaryPhoto} 
                        alt={property.fullAddress}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <span>No Photo</span>
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <span className={`absolute top-3 left-3 px-2 py-1 text-xs font-semibold rounded ${
                      property.status === 'Active' ? 'bg-green-500 text-white' :
                      property.status === 'Pending' ? 'bg-yellow-500 text-white' :
                      'bg-gray-500 text-white'
                    }`}>
                      {property.status}
                    </span>
                    
                    {/* Days on Market */}
                    {property.daysOnMarket > 0 && (
                      <span className="absolute top-3 right-3 px-2 py-1 bg-black/60 text-white text-xs rounded">
                        {property.daysOnMarket} days
                      </span>
                    )}
                  </div>

                  {/* Property Details */}
                  <div className="p-4">
                    <p className="text-2xl font-bold text-[#BCA480] mb-1">
                      {formatPrice(property.listPrice)}
                    </p>
                    
                    <div className="flex items-center text-gray-600 text-sm mb-3">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{property.fullAddress}</span>
                    </div>

                    <div className="flex gap-4 text-gray-600 text-sm">
                      <div className="flex items-center">
                        <Bed className="h-4 w-4 mr-1" />
                        <span>{property.bedrooms} bd</span>
                      </div>
                      <div className="flex items-center">
                        <Bath className="h-4 w-4 mr-1" />
                        <span>{property.bathrooms} ba</span>
                      </div>
                      {property.area > 0 && (
                        <div className="flex items-center">
                          <Square className="h-4 w-4 mr-1" />
                          <span>{property.area.toLocaleString()} sqft</span>
                        </div>
                      )}
                    </div>

                    {/* Property Type */}
                    {property.type && (
                      <p className="text-xs text-gray-500 mt-2">{property.type}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && !error && properties.length === 0 && (
            <div className="text-center py-20" data-testid="no-results">
              <div className="text-gray-400 mb-4">
                <Search className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Properties Found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search filters to find more listings.</p>
              <Button onClick={clearFilters} variant="outline">
                Clear All Filters
              </Button>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8" data-testid="pagination">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <span className="text-gray-600">
                Page {currentPage + 1} of {totalPages}
              </span>
              
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-[#BCA480] to-[#a89470]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Help Finding Your Perfect Home?</h2>
          <p className="text-white/90 mb-6">
            Our experienced agents know the Tennessee market inside and out. Let us help you navigate your home search.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-[#BCA480] hover:bg-gray-100"
            onClick={() => setShowBooking(true)}
          >
            Schedule a Consultation
          </Button>
        </div>
      </section>

      <BookingModal 
        isOpen={showBooking} 
        onClose={() => setShowBooking(false)} 
        bookingType="property_viewing"
      />
    </div>
  );
};

export default MLSPropertySearch;
